// Reusable Export modal.
//
// Renders as a centered Dialog on >=md screens and a bottom Sheet on mobile.
// Owns the post-generation "Download / Open / Share" UX. Filter selection
// stays in the calling component — this modal is presented once the user
// confirms "Generate".

import { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  SHEET_HEADER_CLASS,
  SHEET_BODY_CLASS,
  SHEET_FOOTER_CLASS,
} from "@/components/ui/sheetChrome";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Download,
  Share2,
  ExternalLink,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileText,
  RefreshCw,
} from "lucide-react";
import type { ExportState } from "./useExportPdf";

export interface ExportFilterChip {
  label: string;
  value: string;
}

export interface ExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Localized report title (e.g. "Spray Logs"). */
  reportTitle: string;
  /** Filter chips to display in the preview. */
  filters?: ExportFilterChip[];
  /** Optional summary rows shown in the preview card. */
  summary?: Array<{ label: string; value: string }>;
  /** Optional row-count / details note (e.g. "12 records"). */
  detailsLine?: string;
  state: ExportState;
  error?: string | null;
  canShare: boolean;
  onDownload: () => void;
  onOpen: () => void;
  onShare: () => void;
  /** Re-trigger generation (used by Retry). */
  onRetry?: () => void;
  /** Optional extra body content above the actions. */
  children?: ReactNode;
}

export function ExportModal(props: ExportModalProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const {
    open,
    onOpenChange,
    reportTitle,
    filters = [],
    summary = [],
    detailsLine,
    state,
    error,
    canShare,
    onDownload,
    onOpen,
    onShare,
    onRetry,
    children,
  } = props;

  // Auto-close on Escape is handled by Dialog/Sheet primitives.
  useEffect(() => {
    if (!open) return;
  }, [open]);

  const Body = (
    <div className="flex flex-col gap-4">
      {/* Preview card */}
      <div className="rounded-xl border border-[#b6dded] bg-gradient-to-br from-primary/5 to-primary-blue/5 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-primary text-primary-foreground">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("export.reportPreview")}
            </p>
            <h3 className="truncate text-base font-bold text-foreground">{reportTitle}</h3>
            {detailsLine && (
              <p className="mt-0.5 text-xs text-muted-foreground">{detailsLine}</p>
            )}
          </div>
        </div>

        {filters.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {filters.map((f, i) => (
              <Badge key={i} variant="secondary" className="rounded-md bg-white text-foreground">
                <span className="text-muted-foreground">{f.label}:</span>
                <span className="ml-1 font-semibold">{f.value}</span>
              </Badge>
            ))}
          </div>
        )}

        {summary.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {summary.map((s, i) => (
              <div key={i} className="rounded-md bg-white p-2 shadow-sm">
                <p className="text-[10px] uppercase text-muted-foreground">{s.label}</p>
                <p className="text-sm font-bold text-foreground">{s.value}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {children}

      {/* State strip */}
      {state === "generating" && (
        <div className="flex items-center gap-2 rounded-lg bg-muted/60 p-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          {t("export.generatingState")}
        </div>
      )}
      {state === "ready" && (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          {t("export.readyState")}
        </div>
      )}
      {state === "error" && (
        <div className="flex items-start gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">{t("export.errorTitle")}</p>
            {error && <p className="mt-0.5 text-xs opacity-80">{error}</p>}
          </div>
          {onRetry && (
            <Button size="sm" variant="outline" onClick={onRetry}>
              <RefreshCw className="h-3.5 w-3.5" />
              {t("export.retry")}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  /* El pie va APARTE del cuerpo: en el teléfono, con los chips de filtros, la
     rejilla de resumen y la tira de estado, «Descargar PDF» pasaba del alto de
     la hoja y se iba con el scroll — había que bajar hasta el fondo para
     encontrar el único botón que hace algo. Ahora solo se desplaza el cuerpo y
     el botón se queda pegado al borde de abajo. */
  const Actions = (
    <div className="flex flex-col gap-2 sm:flex-row">
      <Button
        className="h-12 flex-1 text-base"
        onClick={isMobile ? onOpen : onDownload}
        disabled={state !== "ready"}
      >
        <Download className="h-5 w-5" />
        {t("export.downloadAction")}
      </Button>
      {!isMobile && (
        <Button
          variant="outline"
          className="h-12 flex-1 text-base"
          onClick={onOpen}
          disabled={state !== "ready"}
        >
          <ExternalLink className="h-5 w-5" />
          {t("export.openAction")}
        </Button>
      )}
      {(canShare || isMobile) && (
        <Button
          variant="outline"
          className="h-12 flex-1 text-base"
          onClick={onShare}
          disabled={state !== "ready"}
        >
          <Share2 className="h-5 w-5" />
          {t("export.shareAction")}
        </Button>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          /* Corta a propósito: es elegir formato y rango, no una pantalla. */
          fit="short"
          /* Sin redondeo propio: lo pone ya SheetContent, y aquí eran 16px
             contra los 28 de todas las demás hojas. */
          /* Ya no se desplaza el panel entero: se desplaza solo el cuerpo, para
             que el pie de acciones no se vaya con el contenido. */
          className="p-0 overflow-hidden flex flex-col"
          /* La zona segura la pone el pie (SHEET_FOOTER_CLASS); si además la
             pusiera el panel, se contaría dos veces bajo el botón. */
          style={{ paddingBottom: 0 }}
        >
          <SheetHeader className={cn(SHEET_HEADER_CLASS, "shrink-0 text-left")}>
            <SheetTitle>{t("export.modalTitle")}</SheetTitle>
            <SheetDescription>
              {t("export.modalSubtitle")}
            </SheetDescription>
          </SheetHeader>
          <div
            data-sheet-scroll
            className={cn(SHEET_BODY_CLASS, "flex-1 min-h-0 overflow-y-auto pt-4")}
          >
            {Body}
          </div>
          <div className={cn(SHEET_FOOTER_CLASS, "shrink-0 border-t bg-white")}>
            {Actions}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("export.modalTitle")}</DialogTitle>
          <DialogDescription>
            {t("export.modalSubtitle")}
          </DialogDescription>
        </DialogHeader>
        {Body}
        {Actions}
      </DialogContent>
    </Dialog>
  );
}
